import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useCustomSpeechToText } from "../hooks/useCustomSpeechToText";
import { AssessmentShapeChecker } from "../components/AssessmentShapeChecker";
import { AssessmentShell } from "../components/AssessmentShell";
import { useCountDown } from "../../../common/helpers/countHelper";
import { NavItems } from "../../../common/helpers/navigation";
import { getLocale, translate } from "react-i18nify";
import { flatten, intersection, uniq } from "lodash";
import { Env } from "../../../env";
import { playAudioAsync } from "@fassessment/hooks/useAudio";

enum Shape {
    Circle = "circle",
    Rectangle = "rectangle"
}

const alternateWords = {
    [Shape.Circle]: ["clock", "coin", "ring", "círculo", "anillo"],
    [Shape.Rectangle]: ["square", "box", "whiteboard", "book", "rectangulo", "cuadrado", "rectángulo"]
};

const sequence = Object.values(Shape);

const getNextElement = (element: Shape): Shape | undefined => sequence[sequence.indexOf(element) + 1];

const getAcceptedWords = (shape: Shape): string[] => [shape, ...alternateWords[shape]];

const PROMPT_TIMEOUT = 7;

export const AssessmentTestRun = (): JSX.Element => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [shape, setShape] = useState(sequence[0]);
    const [acceptedWords, setAcceptedWords] = useState(getAcceptedWords(shape));
    const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement>();
    const [promptTitle, setPromptTitle] = useState<string>();

    const navigate = useNavigate();

    const goToNextShape = (): void => {
        const next = getNextElement(shape);
        next && setShape(next);
        next && loadAudioForWord(next);
    };

    const toggleNextShape = (wasSuccess: boolean): void => {
        setTimeout(() => {
            if (wasSuccess) {
                setIsSuccess(false);
                goToNextShape();
                setPromptTitle("");
                resetCounter();
            }
        }, 2200);
    };

    const loadAudioForWord = (forShape: Shape): void => {
        const newAudio = new Audio(
            translate("assessments.testPrompt", { word: forShape }).replace("{{blobUrl}}", Env.REACT_APP_BLOB_URL)
        );
        newAudio.load();
        newAudio.loop = false;

        setCurrentAudio(newAudio);
    };

    const handleOnCountDownEnd = (): void => {
        if (!isSuccess) {
            const translatedShape = translate("assessments." + shape).toLowerCase();
            const titleForWord = translate("assessments.theShapeIs", { word: translatedShape });
            setPromptTitle(titleForWord);
            void currentAudio?.play();
        }
    };

    const { results, startSpeechToText, stopSpeechToText, recordingVolume, stream, destroy } = useCustomSpeechToText({
        context: acceptedWords,
        fullAudio: true
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [counter, resetCounter] = useCountDown({ from: PROMPT_TIMEOUT, onDone: handleOnCountDownEnd });

    useEffect(() => {
        setAcceptedWords(getAcceptedWords(shape));
    }, [shape]);

    useEffect(() => {
        const resultsFlatten = uniq(flatten(results.map((r) => r.transcript.toLocaleLowerCase().split(" "))));

        const success = intersection(acceptedWords, resultsFlatten).length > 0;

        if (success) {
            setIsSuccess(success);

            if (!getNextElement(shape)) {
                void stopSpeechToText();
                finishTestRun();
                return;
            }

            toggleNextShape(success);
        }
    }, [results]);

    const finishTestRun = (): void => {
        void stopSpeechToText();
        setTimeout(() => {
            destroy();
            navigate(NavItems.Assessments.Instruction.path);
        }, 3000);
    };

    const handleStart = async (): Promise<void> => {
        const newAudio = new Audio(`${Env.REACT_APP_BLOB_URL}/instructions-${getLocale()}-us/say-shape.mp3`);
        await playAudioAsync(newAudio);

        void startSpeechToText();
        loadAudioForWord(shape);
    };

    useEffect(() => {
        if (stream) {
            void handleStart();
        }
    }, [stream]);

    return (
        <AssessmentShell
            // actually using js || because we need to show the header when prompt is an empty string
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            navBarTitle={promptTitle || translate("assessments.testRunHeader")}
            micVolume={recordingVolume}
            micIsRecording
        >
            <div className="is-flex is-flex-direction-column">
                <AssessmentShapeChecker shape={shape} success={isSuccess} />
            </div>
        </AssessmentShell>
    );
};
